import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RoutePath } from '../../layout/Routes'
import styles from '../CategoryContainer.module.scss'

import { AddOrganisationToCategoryModal } from './AddOrganisationToCategoryModal/AddOrganisationToCategoryModal'
import { CategoryNonprofitActionsMenu } from './CategoryNonprofitActionsMenu/CategoryNonprofitActionsMenu'
import { RemoveOrganisationFromCategoryModal } from './RemoveOrganisationFromCategoryModal/RemoveOrganisationFromCategoryModal'
import {
  Button,
  Copy,
  Flex,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { useBreadcrumbs } from '@percent/partner-dashboard/app/dashboard/layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useQuery, useQueryList, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { CategoryNonprofitsTableItemTypes } from '@percent/partner-dashboard/services/category/category.types'

export function CategoryNonprofitsTable() {
  const { t } = useTranslation()
  const { categoryId } = useRequiredParams()
  const { categoryService } = useServices()
  const { userCan } = usePermissionCheck()
  const canManageCategories = userCan('manageCategories')

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [organisationToRemove, setOrganisationToRemove] = useState<{ id: string; name: string } | null>(null)
  // The list is served by organisation search, which lags behind the link change, so removed rows
  // are hidden locally until a refetch stops returning them.
  const [removedOrganisationIds, setRemovedOrganisationIds] = useState<string[]>([])

  const [{ data, isLoading, totalResults, errorMessage }, { nextPage, previousPage, refresh }] = useQueryList(
    categoryService.getCategoryNonprofitsList,
    {
      category: [categoryId]
    }
  )

  const [{ data: getCategoryData, isLoading: getCategoryIsLoading, errorMessage: getCategoryErrorMsg }] = useQuery(
    categoryService.getCategory,
    {
      id: categoryId
    }
  )

  useBreadcrumbs([
    {
      label: t('breadcrumb.categories'),
      path: RoutePath.CATEGORIES
    },
    {
      label: getCategoryData?.name ?? categoryId ?? ''
    }
  ])

  if (errorMessage || getCategoryErrorMsg) {
    return <ErrorView errorMessage={errorMessage ?? getCategoryErrorMsg} />
  }

  const visibleOrganisations = (data ?? []).filter(({ id }) => !removedOrganisationIds.includes(id))
  const staleRemovedCount = (data ?? []).length - visibleOrganisations.length
  const visibleTotalResults = totalResults === null ? null : Math.max(totalResults - staleRemovedCount, 0)

  const handleRemoveSuccess = (removedOrganisationId: string) => {
    setRemovedOrganisationIds(currentIds => [...currentIds, removedOrganisationId])
    refresh()
  }

  const handleAddSuccess = () => {
    setRemovedOrganisationIds([])
    refresh()
  }

  const columns = [
    { id: t('table.header.id'), props: { width: '30%' } },
    { id: t('table.header.name'), props: { width: '24%' } },
    { id: t('table.header.registryId'), props: { width: '18%' } },
    { id: t('table.header.website'), props: { width: '26%' } },
    { id: '', props: { width: '2%' } }
  ]

  return (
    <>
      {canManageCategories && (
        <Flex justify="flex-end" mb="sm">
          <Button
            variant="primary"
            onClick={() => setIsAddModalOpen(true)}
            data-testid="add-organisation-to-category-button"
          >
            {t('categories.addOrganisation.button')}
          </Button>
        </Flex>
      )}

      <TableProvider
        columns={columns}
        totalResults={visibleTotalResults}
        isLoading={isLoading ?? getCategoryIsLoading}
        illustration="no-results"
        emptyTableTitle={t('typography.noCategoryNonprofitsTable')}
      >
        <Table>
          <TableBody>
            {visibleOrganisations.map((organisation: CategoryNonprofitsTableItemTypes) => {
              // Not every organisation has a display name, so fall back to the registered name.
              const organisationName = organisation.displayName || organisation.name

              return (
                <TableRow key={`category-nonprofit-${organisation.id}`}>
                  <TableCell>
                    <Copy message={t('typography.copyId')} text={organisation.id}>
                      <span className={styles.spanId}>{organisation.id}</span>
                    </Copy>
                  </TableCell>
                  <TableCell>{organisationName}</TableCell>
                  <TableCell>{organisation.registryId || t('status.notAvailable')}</TableCell>
                  <TableCell>{organisation.website || t('status.notAvailable')}</TableCell>
                  <CategoryNonprofitActionsMenu
                    organisationId={organisation.id}
                    organisationName={organisationName}
                    rawJsonData={organisation}
                    onRemove={canManageCategories ? setOrganisationToRemove : undefined}
                  />
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: visibleTotalResults ?? undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>

      {canManageCategories && isAddModalOpen && (
        <AddOrganisationToCategoryModal
          categoryId={categoryId}
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={handleAddSuccess}
        />
      )}
      {canManageCategories && organisationToRemove && (
        <RemoveOrganisationFromCategoryModal
          categoryId={categoryId}
          organisation={organisationToRemove}
          open
          onClose={() => setOrganisationToRemove(null)}
          onSuccess={handleRemoveSuccess}
        />
      )}
    </>
  )
}
